import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createApplication, auth } from '../../database/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import ApplicationForm from './ApplicationForm';
import RegistrationOtpPrompt from '../../components/auth/RegistrationOtpPrompt';
const businessStages = ['Idea', 'Early Startup', 'Growth', 'Established'];
const programs = [
  'Business Health Check',
  'Design A Business Training',
  'Scale Your Business Coaching',
  'Introduction to Costing & Management Accounting',
];

const initialForm = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  businessStage: '',
  program: '',
  location: '',
  organization: '',
  terms: false,
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
};

const toastVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
};

const formSectionVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

const RegistrationPage = ({ open = true, onClose, onSubmit }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showOtpPrompt, setShowOtpPrompt] = useState(false);
  const [applicantId, setApplicantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [user, setUser] = useState(null);
  const [errorPopup, setErrorPopup] = useState({
    open: false,
    title: 'Registration error',
    message: '',
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const generateApplicantId = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `APP-${timestamp}-${random}`;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.terms) {
      setErrorPopup({
        open: true,
        title: 'Terms not accepted',
        message: 'You must agree to the terms.',
      });
      return;
    }

    // Show OTP prompt for registration verification
    // The OTP prompt will handle submission via Cloud Function
    setShowOtpPrompt(true);
  };

  const handleOtpVerified = async ({ success, registrationId }) => {
    if (!success) {
      setShowOtpPrompt(false);
      return;
    }

    setLoading(true);
    setShowOtpPrompt(false);

    try {
      // After OTP verification, proceed with application creation
      const newApplicantId = generateApplicantId();
      setApplicantId(newApplicantId);

      const applicationData = {
        applicantId: newApplicantId,
        clientId: newApplicantId,
        userId: newApplicantId,
        firstname: form.firstname,
        lastname: form.lastname,
        email: form.email,
        phone: form.phone,
        businessStage: form.businessStage,
        program: form.program,
        location: form.location,
        organization: form.organization,
        status: 'pending',
        currentStep: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        registrationId, // Store the registration ID for reference
      };

      await createApplication(applicationData, newApplicantId);

      setClientId(newApplicantId);
      setShowApplicationForm(true);
    } catch (error) {
      setErrorPopup({
        open: true,
        title: 'Registration error',
        message: error?.message || 'An error occurred during registration',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpCancel = () => {
    setShowOtpPrompt(false);
  };

  const handleApplicationComplete = async applicationData => {
    setLoading(true);
    try {
      if (onSubmit) {
        await onSubmit({
          ...form,
          ...applicationData,
          applicantId,
          clientId,
          userId: user?.uid,
        });
      }

      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setForm(initialForm);
        setShowApplicationForm(false);
        if (onClose) onClose();
      }, 2000);
    } catch (error) {
      setErrorPopup({
        open: true,
        title: 'Submission error',
        message: 'An error occurred while completing the application',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && onClose) {
      onClose();
    }
  };

  if (!open) return null;

  if (showApplicationForm) {
    return (
      <ApplicationForm
        applicantId={applicantId}
        clientId={clientId}
        userId={user?.uid || clientId}
        userEmail={form.email}
        onClose={() => setShowApplicationForm(false)}
        onComplete={handleApplicationComplete}
      />
    );
  }

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key="registration-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <motion.div
            key="registration-modal"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative max-h-[90vh] w-[92%] max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl sm:w-full"
          >
            <AnimatePresence>
              {showToast && (
                <motion.div
                  key="toast"
                  variants={toastVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="absolute left-1/2 top-4 z-50 -translate-x-1/2 transform rounded-lg bg-green-600 px-6 py-3 text-white shadow-lg"
                >
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Application submitted successfully!
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="p-6 md:p-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.1 }}
                className="absolute right-4 top-4 text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none disabled:opacity-50"
                onClick={handleClose}
                aria-label="Close"
                disabled={loading}
              >
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </motion.button>

              <motion.div
                variants={formSectionVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.1, duration: 0.2 }}
                className="mb-8 text-center"
              >
                <h2 className="mb-2 text-2xl font-bold text-blue-600 md:text-3xl">
                  Start Your Journey
                </h2>
                <p className="text-sm text-gray-600 md:text-base">
                  Join our Entrepreneur Development Program
                </p>
              </motion.div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <motion.div
                  variants={formSectionVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.15, duration: 0.2 }}
                  className="grid grid-cols-1 gap-4 md:grid-cols-2"
                >
                  {[
                    { field: 'firstname', label: 'First Name', icon: 'user' },
                    { field: 'lastname', label: 'Last Name', icon: 'user' },
                  ].map(({ field, label, icon }) => (
                    <div key={field} className="relative">
                      <label
                        htmlFor={field}
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        {label} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                          <svg
                            width="18"
                            height="18"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle cx="12" cy="7" r="4" />
                            <path d="M5.5 21a7.5 7.5 0 0 1 13 0" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          name={field}
                          id={field}
                          value={form[field]}
                          onChange={handleChange}
                          className="w-full rounded-xl border border-gray-300 bg-blue-50/50 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-all duration-150 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                          placeholder={`Enter your ${label.toLowerCase()}`}
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  variants={formSectionVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.2, duration: 0.2 }}
                  className="relative"
                >
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                      <svg
                        width="18"
                        height="18"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <rect x="2" y="5" width="20" height="14" rx="2" />
                        <path d="m22 5-10 7L2 5" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-gray-300 bg-blue-50/50 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-all duration-150 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter your email address"
                      required
                      disabled={loading}
                    />
                  </div>
                </motion.div>

                <motion.div
                  variants={formSectionVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.35, duration: 0.2 }}
                  className="relative"
                >
                  <label htmlFor="phone" className="mb-2 block text-sm font-medium text-gray-700">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                      <svg
                        width="18"
                        height="18"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path d="M22 16.92V19a2 2 0 01-2.18 2 19.8 19.8 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.8 19.8 0 01-3.07-8.67A2 2 0 014.11 2h2.09a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9a16 16 0 006.92 6.92l1.27-.36a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                      </svg>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-gray-300 bg-blue-50/50 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-all duration-150 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter your phone number"
                      required
                      disabled={loading}
                    />
                  </div>
                </motion.div>

                <motion.div
                  variants={formSectionVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.4, duration: 0.2 }}
                  className="grid grid-cols-1 gap-4 md:grid-cols-2"
                >
                  {[
                    { name: 'businessStage', options: businessStages, label: 'Business Stage' },
                    { name: 'program', options: programs, label: 'Interested Program' },
                  ].map(({ name, options, label }) => (
                    <div className="relative" key={name}>
                      <label
                        htmlFor={name}
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        {label} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                          <svg
                            width="18"
                            height="18"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <rect x="3" y="7" width="18" height="13" rx="2" />
                            <path d="M16 3v4" />
                            <path d="M8 3v4" />
                          </svg>
                        </div>
                        <select
                          name={name}
                          id={name}
                          value={form[name]}
                          onChange={handleChange}
                          className="w-full cursor-pointer appearance-none rounded-xl border border-gray-300 bg-blue-50/50 py-3 pl-10 pr-10 text-gray-900 transition-all duration-150 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                          required
                          disabled={loading}
                        >
                          <option value="" disabled>
                            Select {label.toLowerCase()}...
                          </option>
                          {options.map(opt => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                          <svg
                            width="18"
                            height="18"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  variants={formSectionVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.45, duration: 0.2 }}
                  className="grid grid-cols-1 gap-4 md:grid-cols-2"
                >
                  {[
                    { field: 'location', label: 'Location', icon: 'map' },
                    { field: 'organization', label: 'Organization', icon: 'building' },
                  ].map(({ field, label, icon }) => (
                    <div key={field} className="relative">
                      <label
                        htmlFor={field}
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        {label} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                          <svg
                            width="18"
                            height="18"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            {icon === 'map' ? (
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            ) : (
                              <path d="M3 21h18" />
                            )}
                            {icon === 'map' ? (
                              <circle cx="12" cy="10" r="3" />
                            ) : (
                              <path d="M5 21V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v14" />
                            )}
                          </svg>
                        </div>
                        <input
                          type="text"
                          name={field}
                          id={field}
                          value={form[field]}
                          onChange={handleChange}
                          className="w-full rounded-xl border border-gray-300 bg-blue-50/50 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-all duration-150 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                          placeholder={`Enter your ${label.toLowerCase()}`}
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  variants={formSectionVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.5, duration: 0.2 }}
                  className="flex items-start space-x-3"
                >
                  <input
                    type="checkbox"
                    name="terms"
                    id="terms"
                    checked={form.terms}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-200"
                    required
                    disabled={loading}
                  />
                  <label htmlFor="terms" className="text-sm leading-relaxed text-gray-700">
                    I agree to the{' '}
                    <button
                      type="button"
                      className="font-medium text-blue-600 underline hover:text-blue-800"
                    >
                      Terms of Service
                    </button>{' '}
                    and{' '}
                    <button
                      type="button"
                      className="font-medium text-blue-600 underline hover:text-blue-800"
                    >
                      Privacy Policy
                    </button>
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  transition={{ duration: 0.1 }}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-green-500 px-6 py-4 font-semibold text-white shadow-lg transition-all duration-150 hover:from-blue-600 hover:to-green-600 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 disabled:from-gray-400 disabled:to-gray-500 disabled:hover:scale-100"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-3">
                      <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <span className="text-lg">Continue to Application</span>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {errorPopup.open && (
          <motion.div
            key="error-backdrop"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          >
            <motion.div
              key="error-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:w-full"
              role="alertdialog"
              aria-labelledby="registration-error-title"
              aria-describedby="registration-error-desc"
            >
              <div className="mb-4 flex items-start gap-3">
                <div className="mt-0.5 text-red-600">
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </div>
                <div>
                  <h3 id="registration-error-title" className="text-lg font-semibold text-gray-900">
                    {errorPopup.title}
                  </h3>
                  <p id="registration-error-desc" className="mt-1 text-sm text-gray-700">
                    {errorPopup.message}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setErrorPopup({ ...errorPopup, open: false })}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  OK
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <RegistrationOtpPrompt
        registrationData={form}
        open={showOtpPrompt}
        onVerified={handleOtpVerified}
        onCancel={handleOtpCancel}
      />
    </>
  );
};

export default RegistrationPage;
